import { FormFieldsByUniqueID } from 'components/form/formTypes'
import {
  DetectionConfigurationFormData,
  DetectionConfigurationFormFields,
} from 'features/anomalyDetection/forms/detectionConfigurationFormFields'
import {
  DetectionModelFormData,
  DetectionModelFormFields,
} from 'features/anomalyDetection/forms/detectionModelFormFields'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, { createContext, useEffect, useMemo, useState } from 'react'
import {
  BaseSensorEntry,
  DetectionConfigurationData,
  DetectionConfigurationLoadedEntry,
  DropdownOption,
  RolodexEntryType,
} from 'types'

interface EditDetectionConfigurationDataContextProps {
  siteID: string
  detectionConfigurationId: string
  isNewConfiguration: boolean
  detectionConfigurationLoadedEntry:
    | DetectionConfigurationLoadedEntry
    | undefined
  mutateDetectionConfigurationLoadedEntry: () => void
  detectionConfigurationData: DetectionConfigurationFormData
  setDetectionConfigurationData: React.Dispatch<
    React.SetStateAction<DetectionConfigurationFormData>
  >
  detectionConfigurationFormFields: FormFieldsByUniqueID[]
  sensorOptions: DropdownOption[]
  isSensorsLoading: boolean
  detectionModelData: DetectionModelFormData
  setDetectionModelData: React.Dispatch<
    React.SetStateAction<DetectionModelFormData>
  >
  detectionModelFormFields: FormFieldsByUniqueID[]
  detectionConfiguration: DetectionConfigurationData
  setDetectionConfiguration: React.Dispatch<
    React.SetStateAction<DetectionConfigurationData>
  >
}

export const EditDetectionConfigurationDataContext =
  createContext<EditDetectionConfigurationDataContextProps>(null!)

export const EditDetectionConfigurationDataLayer: React.FC<{
  siteID: string
  detectionConfigurationId: string
  children: React.ReactNode
}> = ({ siteID, detectionConfigurationId, children }) => {
  const [detectionConfigurationData, setDetectionConfigurationData] =
    useState<DetectionConfigurationFormData>({
      configName: '',
      targetSensorId: '',
      pollingFrequency: 5,
      isEnabled: 'yes',
    })

  const [detectionModelData, setDetectionModelData] =
    useState<DetectionModelFormData>({
      modelName: '',
      isEnabled: 'yes',
    })
  const [detectionConfiguration, setDetectionConfiguration] =
    useState<DetectionConfigurationData>({
      name: '',
      target_sensor_entry_id: '',
      polling_frequency_minutes: 5,
      is_enabled: true,
      detection_models: [],
    })

  const {
    data: detectionConfigurationLoadedEntry,
    mutate: mutateDetectionConfigurationLoadedEntry,
    isLoading: isDetectionConfigurationLoading,
  } = useGetRolodexEntriesByLoadOptions<DetectionConfigurationLoadedEntry[]>({
    type: [RolodexEntryType.DETECTION_CONFIGURATION],
    ids: [detectionConfigurationId],
    limit: 1,
  })

  useEffect(() => {
    if (
      detectionConfigurationLoadedEntry &&
      detectionConfigurationLoadedEntry.length > 0
    ) {
      const entry = detectionConfigurationLoadedEntry[0].entry
      setDetectionConfigurationData((prev) => ({
        ...prev,
        configName: entry.data.name ?? '',
        targetSensorId: entry.data.target_sensor_entry_id ?? '',
        pollingFrequency: entry.data.polling_frequency_minutes,
        isEnabled: entry.data.is_enabled ? 'yes' : 'no',
      }))
      setDetectionConfiguration(entry.data)
    }
  }, [detectionConfigurationLoadedEntry])

  const { data: sensorData, isLoading: isSensorsLoading } =
    useGetRolodexEntriesByLoadOptions<
      { entry: BaseSensorEntry; entry_json: string }[]
    >({
      type: [RolodexEntryType.DATA_POINTS],
      limit: 10000,
    })

  const sensorOptions: DropdownOption[] = useMemo(() => {
    if (!sensorData) return []

    const uniqueSensorIds = new Set<string>()
    const options: DropdownOption[] = []

    sensorData.forEach((item) => {
      if (item.entry?.id && !uniqueSensorIds.has(item.entry.id)) {
        uniqueSensorIds.add(item.entry.id)
        const sensorName = item.entry.data?.name || item.entry.id
        options.push({
          label: sensorName,
          value: item.entry.id,
        })
      }
    })

    return options
  }, [sensorData])

  const detectionConfigurationFormFields = useMemo(
    () => [
      {
        uniqueID: 'detectionConfigurationForm',
        formFields: DetectionConfigurationFormFields({
          data: detectionConfigurationData,
          sensorOptions,
        }),
      },
    ],
    [detectionConfigurationData, sensorOptions]
  )

  const detectionModelFormFields = useMemo(
    () => [
      {
        uniqueID: 'detectionModelForm',
        formFields: DetectionModelFormFields({
          data: detectionModelData,
        }),
      },
    ],
    [detectionModelData]
  )

  return (
    <EditDetectionConfigurationDataContext.Provider
      value={{
        siteID,
        detectionConfigurationId,
        isNewConfiguration:
          isDetectionConfigurationLoading ||
          detectionConfigurationLoadedEntry?.length === 0,
        detectionConfigurationLoadedEntry:
          detectionConfigurationLoadedEntry?.[0],
        mutateDetectionConfigurationLoadedEntry,
        detectionConfigurationData,
        setDetectionConfigurationData,
        detectionConfigurationFormFields,
        sensorOptions,
        isSensorsLoading,
        detectionModelData,
        setDetectionModelData,
        detectionModelFormFields,
        detectionConfiguration,
        setDetectionConfiguration,
      }}>
      {children}
    </EditDetectionConfigurationDataContext.Provider>
  )
}
